import { useEffect, useRef, useState, type ChangeEvent, type FocusEvent, type InputHTMLAttributes } from "react";

type NumericInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type">;

function displayValue(value: NumericInputProps["value"]): string {
  if (value == null) return "";
  return Array.isArray(value) ? value.join(",") : String(value);
}

export function NumericInput({ value, defaultValue, onChange, onFocus, onBlur, ...props }: NumericInputProps) {
  const controlled = value !== undefined;
  const focused = useRef(false);
  const [draft, setDraft] = useState(() => displayValue(value));

  useEffect(() => {
    if (!focused.current) setDraft(displayValue(value));
  }, [value]);

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    if (controlled) setDraft(event.target.value);
    onChange?.(event);
  }

  function handleFocus(event: FocusEvent<HTMLInputElement>) {
    focused.current = true;
    onFocus?.(event);
  }

  function handleBlur(event: FocusEvent<HTMLInputElement>) {
    focused.current = false;
    onBlur?.(event);
    if (controlled) setDraft(displayValue(value));
  }

  return <input
    {...props}
    type="number"
    value={controlled ? draft : undefined}
    defaultValue={controlled ? undefined : defaultValue}
    onChange={handleChange}
    onFocus={handleFocus}
    onBlur={handleBlur}
  />;
}
